import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Bell,
  Zap,
  BarChart3,
  RefreshCw,
  Loader2,
  Shield,
  XCircle,
  Timer,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import { TACO_MASCOT_URL } from './adminUtils';

const TacoHealthTab = ({ api }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState('7');

  const fetchHealthData = useCallback(async (showToast = false) => {
    try {
      const response = await api().get(`/taco/v2/health-panel?days=${days}`);
      setHealthData(response.data);
      if (showToast) toast.success('Health data refreshed');
    } catch (error) {
      console.error('Failed to fetch health data:', error);
      toast.error('Failed to load Taco health data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, days]);

  useEffect(() => {
    setLoading(true);
    fetchHealthData();
  }, [days]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHealthData(true);
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getHealthScoreBg = (status) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-100 border-emerald-300';
      case 'good': return 'bg-emerald-50 border-emerald-200';
      case 'needs_attention': return 'bg-amber-50 border-amber-200';
      case 'critical': return 'bg-red-50 border-red-200';
      default: return 'bg-zinc-50 border-zinc-200';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-zinc-100 text-zinc-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!healthData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <XCircle className="h-12 w-12 text-zinc-300 mb-4" />
        <p className="text-zinc-500">Failed to load health data</p>
        <Button variant="outline" onClick={handleRefresh} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  const { alerts, active_alerts, escalations, notifications, scan_health, health_score } = healthData;

  return (
    <div className="space-y-6" data-testid="taco-health-content">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={TACO_MASCOT_URL} alt="Taco" className="h-14 w-14 rounded-full border-2 border-emerald-400" />
          <div>
            <h2 className="text-lg font-semibold text-zinc-900">Taco AI Health Panel</h2>
            <p className="text-sm text-zinc-500">Governance metrics and system health monitoring</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32" data-testid="health-period-select">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-health-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Health Score Card */}
      <Card className={`border-2 ${getHealthScoreBg(health_score.status)}`}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className={`text-5xl font-bold ${getHealthScoreColor(health_score.score)}`} data-testid="health-score">
                  {health_score.score}
                </div>
                <div className="text-sm text-zinc-500 mt-1">Health Score</div>
              </div>
              <div className="h-20 w-px bg-zinc-200" />
              <div>
                <Badge
                  className={`text-sm px-3 py-1 ${
                    health_score.status === 'excellent' ? 'bg-emerald-600' :
                    health_score.status === 'good' ? 'bg-emerald-500' :
                    health_score.status === 'needs_attention' ? 'bg-amber-500' :
                    'bg-red-500'
                  } text-white`}
                  data-testid="health-status-badge"
                >
                  {health_score.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <div className="mt-3 space-y-1">
                  {health_score.issues.map((issue, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                      {issue.includes('normally') ? (
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      )}
                      {issue}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-right text-sm text-zinc-500">
              <div>Updated: {new Date(healthData.generated_at).toLocaleString()}</div>
              <div>Period: Last {healthData.period_days} days</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Alerts 7d */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto text-amber-500 mb-2" />
            <div className="text-2xl font-bold text-zinc-900" data-testid="alerts-7d">
              {alerts.total_7d}
            </div>
            <div className="text-xs text-zinc-500">Alerts ({days}d)</div>
          </CardContent>
        </Card>

        {/* Auto-Resolved Rate */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 text-center">
            <Zap className="h-6 w-6 mx-auto text-emerald-500 mb-2" />
            <div className="text-2xl font-bold text-emerald-600" data-testid="auto-resolve-rate">
              {alerts.auto_resolve_rate}%
            </div>
            <div className="text-xs text-zinc-500">Auto-Resolved</div>
          </CardContent>
        </Card>

        {/* Avg Resolve Time */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 text-center">
            <Timer className="h-6 w-6 mx-auto text-blue-500 mb-2" />
            <div className="text-2xl font-bold text-blue-600" data-testid="avg-resolve-time">
              {alerts.avg_resolve_minutes < 60
                ? `${Math.round(alerts.avg_resolve_minutes)}m`
                : `${(alerts.avg_resolve_minutes / 60).toFixed(1)}h`}
            </div>
            <div className="text-xs text-zinc-500">Avg Resolve</div>
          </CardContent>
        </Card>

        {/* Escalations */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-6 w-6 mx-auto text-orange-500 mb-2" />
            <div className="text-2xl font-bold text-orange-600" data-testid="escalations-7d">
              {escalations.total_7d}
            </div>
            <div className="text-xs text-zinc-500">Escalations</div>
          </CardContent>
        </Card>

        {/* Notification Success */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 text-center">
            <Bell className="h-6 w-6 mx-auto text-purple-500 mb-2" />
            <div className="text-2xl font-bold text-purple-600" data-testid="notification-success">
              {notifications.success_rate}%
            </div>
            <div className="text-xs text-zinc-500">Notif Success</div>
          </CardContent>
        </Card>

        {/* Scan Health */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 text-center">
            <Activity className={`h-6 w-6 mx-auto ${scan_health.scheduler_running ? 'text-emerald-500' : 'text-red-500'} mb-2`} />
            <div className={`text-2xl font-bold ${scan_health.scheduler_running ? 'text-emerald-600' : 'text-red-600'}`} data-testid="scan-health">
              {scan_health.scheduler_running ? 'ON' : 'OFF'}
            </div>
            <div className="text-xs text-zinc-500">Scheduler</div>
          </CardContent>
        </Card>
      </div>

      {/* Active Alerts Summary */}
      <Card className="border-zinc-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-500" />
            Active Alerts (Unresolved)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Badge className={getSeverityColor('high')}>High</Badge>
              <span className="text-lg font-semibold" data-testid="active-high">{active_alerts.high}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getSeverityColor('medium')}>Medium</Badge>
              <span className="text-lg font-semibold" data-testid="active-medium">{active_alerts.medium}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getSeverityColor('low')}>Low</Badge>
              <span className="text-lg font-semibold" data-testid="active-low">{active_alerts.low}</span>
            </div>
            <div className="ml-auto text-sm text-zinc-500">
              Total: <span className="font-semibold">{active_alerts.total}</span> unresolved
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Alerts by Type */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-500" />
              Alerts by Type ({days}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(alerts.by_type || {}).map(([type, count]) => {
                const maxCount = Math.max(...Object.values(alerts.by_type || { _: 1 }), 1);
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={type} className="space-y-1" data-testid={`alert-type-${type.toLowerCase()}`}>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">{type.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
              {Object.keys(alerts.by_type || {}).length === 0 && (
                <div className="text-center text-zinc-400 py-4">No alerts in this period</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Alerts by Severity */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Alerts by Severity ({days}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {['high', 'medium', 'low'].map((severity) => {
                const count = alerts.by_severity?.[severity] || 0;
                const total = alerts.total_7d || 1;
                const percentage = (count / total) * 100;
                return (
                  <div key={severity} className="space-y-1" data-testid={`alert-severity-${severity}`}>
                    <div className="flex justify-between text-sm">
                      <span className={`font-medium ${
                        severity === 'high' ? 'text-red-600' :
                        severity === 'medium' ? 'text-amber-600' :
                        'text-blue-600'
                      }`}>
                        {severity.charAt(0).toUpperCase() + severity.slice(1)}
                      </span>
                      <span className="font-medium">{count} ({Math.round(percentage)}%)</span>
                    </div>
                    <Progress
                      value={percentage}
                      className={`h-2 ${
                        severity === 'high' ? '[&>div]:bg-red-500' :
                        severity === 'medium' ? '[&>div]:bg-amber-500' :
                        '[&>div]:bg-blue-500'
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      <Card className="border-zinc-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Daily Alert Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 h-40 overflow-x-auto pb-2">
            {(alerts.daily_trend || []).map((day, idx) => {
              const maxVal = Math.max(...alerts.daily_trend.map(d => Math.max(d.created, d.resolved)), 1);
              const createdHeight = (day.created / maxVal) * 100;
              const resolvedHeight = (day.resolved / maxVal) * 100;
              return (
                <div key={day.date} className="flex-shrink-0 flex flex-col items-center gap-1 min-w-[60px]" data-testid={`trend-day-${idx}`}>
                  <div className="flex items-end gap-1 h-28">
                    <div
                      className="w-5 bg-amber-400 rounded-t transition-all"
                      style={{ height: `${createdHeight}%`, minHeight: day.created > 0 ? '4px' : '0' }}
                      title={`Created: ${day.created}`}
                    />
                    <div
                      className="w-5 bg-emerald-400 rounded-t transition-all"
                      style={{ height: `${resolvedHeight}%`, minHeight: day.resolved > 0 ? '4px' : '0' }}
                      title={`Resolved: ${day.resolved}`}
                    />
                  </div>
                  <div className="text-xs text-zinc-500 rotate-0">{day.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-4 justify-center">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 rounded bg-amber-400" />
              <span className="text-zinc-600">Created</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 rounded bg-emerald-400" />
              <span className="text-zinc-600">Resolved</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications & Scan Details */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Notifications */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-5 w-5 text-purple-500" />
              Notification Delivery ({days}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Total Sent</span>
                <span className="font-semibold">{notifications.total_7d}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Failed</span>
                <span className="font-semibold text-red-600">{notifications.failed_7d}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Success Rate</span>
                <Badge className={notifications.success_rate >= 95 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                  {notifications.success_rate}%
                </Badge>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="text-sm font-medium text-zinc-700 mb-2">By Channel</div>
                {Object.entries(notifications.by_channel || {}).map(([channel, data]) => (
                  <div key={channel} className="flex justify-between items-center py-1 text-sm">
                    <span className="capitalize text-zinc-600">{channel}</span>
                    <span>
                      {data.sent} sent
                      {data.failed > 0 && <span className="text-red-500 ml-2">({data.failed} failed)</span>}
                    </span>
                  </div>
                ))}
                {Object.keys(notifications.by_channel || {}).length === 0 && (
                  <div className="text-zinc-400 text-sm">No notifications sent</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scan Health Details */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bot className="h-5 w-5 text-emerald-500" />
              Intelligence Engine Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Scheduler Status</span>
                <Badge className={scan_health.scheduler_running ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                  {scan_health.scheduler_running ? 'Running' : 'Stopped'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Scan Interval</span>
                <span className="font-semibold">{scan_health.scan_interval_minutes} min</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Next Scan</span>
                <span className="text-sm">
                  {scan_health.next_scan
                    ? new Date(scan_health.next_scan).toLocaleTimeString()
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-600">Alerts Generated ({days}d)</span>
                <span className="font-semibold">{scan_health.alerts_created_7d}</span>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="text-sm font-medium text-zinc-700 mb-2">Detection Rules</div>
                <div className="grid grid-cols-1 gap-1 text-sm text-zinc-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Unassigned Booking (HIGH)
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Late Arrival (HIGH)
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Missing Arrival Photo (MEDIUM)
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Incomplete Checklist (MEDIUM)
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Revenue Variance (MEDIUM)
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TacoHealthTab;
